/**
 * Recurrence estimator (ENGINE-SPEC §3.3, §11.3): the D6 fix. For each position the distance back
 * to the previous occurrence of its key is observed, right-censored at the structural boundary
 * (section or document start). A Kaplan–Meier curve over those distances gives R(ℓ), the
 * probability that an item recurred within the previous ℓ positions, independent of document
 * length. Seed statistics at their own reference size follow exactly (§3.3 step 5).
 */

/** 32-bit finaliser (murmur3 fmix32). */
export function fmix32(h: number): number {
	let x = h >>> 0;
	x ^= x >>> 16;
	x = Math.imul(x, 0x85ebca6b) >>> 0;
	x ^= x >>> 13;
	x = Math.imul(x, 0xc2b2ae35) >>> 0;
	x ^= x >>> 16;
	return x >>> 0;
}

/** 53-bit key from two 32-bit lanes (§11.3). Never returns 0. */
export function mix53(h1: number, h2: number): number {
	return (h1 >>> 0) * 2097152 + (h2 >>> 11) + 1;
}

/** Hash the w ids starting at i (two independent lanes). */
export function hashWindow(ids: ArrayLike<number>, i: number, w: number): number {
	let h1 = 0x9e3779b9;
	let h2 = 0x85ebca77;
	for (let j = 0; j < w; j++) {
		const id = (ids[i + j] as number) + 1;
		h1 = fmix32(h1 ^ Math.imul(id, 0xcc9e2d51));
		h2 = fmix32(h2 + Math.imul(id ^ 0x1b873593, 0x27d4eb2f) + j);
	}
	return mix53(h1, h2);
}

/** Open-addressing map from 53-bit keys to last position (and an optional auxiliary value). */
export class LastTable {
	private keys: Float64Array;
	private pos: Int32Array;
	private aux: Uint32Array;
	/** Free per-key counter for callers (e.g. productive-recurrence counts); addressed by `lastSlot`. */
	readonly extra: Uint16Array;
	private readonly slotKeys: Float64Array;
	private readonly mask: number;
	/** Slot index of the key touched by the last `getSet`. */
	lastSlot = 0;
	size = 0;
	/** Set by `getSet`: previous position (−1 if none) and auxiliary value. */
	prevPos = -1;
	prevAux = 0;
	constructor(expected: number) {
		let cap = 1024;
		while (cap < expected * 2) cap *= 2;
		this.keys = new Float64Array(cap);
		this.pos = new Int32Array(cap);
		this.aux = new Uint32Array(cap);
		this.extra = new Uint16Array(cap);
		this.slotKeys = this.keys;
		this.mask = cap - 1;
	}
	/** Keys with the largest `extra` counters (descending), for second-pass evidence. */
	topByExtra(count: number): Array<{ key: number; extra: number }> {
		const out: Array<{ key: number; extra: number }> = [];
		for (let i = 0; i < this.slotKeys.length; i++) {
			const e = this.extra[i] as number;
			if (e > 0 && (this.slotKeys[i] as number) !== 0)
				out.push({ key: this.slotKeys[i] as number, extra: e });
		}
		out.sort((a, b) => b.extra - a.extra);
		return out.slice(0, count);
	}
	/** Looks up `key`, records (position, aux) as the new last occurrence, exposes the previous one. */
	getSet(key: number, position: number, aux = 0): void {
		// `key | 0` is ToInt32(key), identical to `(key % 2^32) | 0` for the integer-valued 53-bit keys but avoids the slow fmod
		let i = (Math.imul(key | 0, 0x9e3779b1) >>> 0) & this.mask;
		for (;;) {
			const k = this.keys[i] as number;
			if (k === 0) {
				this.keys[i] = key;
				this.pos[i] = position;
				this.aux[i] = aux;
				this.size++;
				this.lastSlot = i;
				this.prevPos = -1;
				this.prevAux = 0;
				return;
			}
			if (k === key) {
				this.lastSlot = i;
				this.prevPos = this.pos[i] as number;
				this.prevAux = this.aux[i] as number;
				this.pos[i] = position;
				this.aux[i] = aux;
				return;
			}
			i = (i + 1) & this.mask;
		}
	}
}

export interface RecurrenceCurve {
	lmax: number;
	/** Number of positions at risk at lag t (index 0..lmax; [0] = all positions with any lookback). */
	atRisk: Float64Array;
	/** Recurrence events at distance t (index 1..lmax). */
	events: Float64Array;
	/** Survival S(t) = P(no previous occurrence within the previous t positions), S[0] = 1. */
	S: Float64Array;
	/** Greenwood variance of S(t). */
	varS: Float64Array;
	/** Total positions with lookback ≥ 1. */
	n: number;
}

export interface RecurrenceScan {
	curve: RecurrenceCurve;
	/** Only when `fills` were given: curve where the event is "nearest previous occurrence has a DIFFERENT fill" (same-fill recurrences are censored at their distance). */
	prodCurve?: RecurrenceCurve;
	/** Only with fills: per position, distance if that recurrence was productive (different fill) and ≤ lmax, else 0. */
	prodDist?: Uint16Array | Uint32Array;
	table: LastTable;
	/** Per position: distance to the previous in-boundary occurrence if ≤ lmax, else 0. */
	dist: Uint16Array | Uint32Array;
}

/**
 * One pass over `keys` (0 = skip this position). `boundary[i]` is the first position index that
 * position i may look back to (its section start); pass null for a single document-wide section.
 * Distances above `lmax` are censored at lmax.
 */
export function scanRecurrence(
	keys: Float64Array,
	boundary: Int32Array | null,
	lmax: number,
	fills?: Uint32Array
): RecurrenceScan {
	const n = keys.length;
	const table = new LastTable(n);
	const dist = lmax < 65536 ? new Uint16Array(n) : new Uint32Array(n);
	const obsHist = new Float64Array(lmax + 1); // observed time histogram (event or censor)
	const evHist = new Float64Array(lmax + 1);
	const prodEv = fills ? new Float64Array(lmax + 1) : null;
	const prodDist = fills ? (lmax < 65536 ? new Uint16Array(n) : new Uint32Array(n)) : undefined;
	let total = 0;
	for (let i = 0; i < n; i++) {
		const key = keys[i] as number;
		if (key === 0) continue;
		const b = boundary ? (boundary[i] as number) : 0;
		table.getSet(key, i, fills ? (fills[i] as number) : 0);
		const a = i - b; // available lookback
		if (a <= 0) continue;
		total++;
		const j = table.prevPos;
		if (j >= b && j >= 0) {
			const d = i - j;
			if (d <= lmax) {
				dist[i] = d;
				if (fills && prodEv && prodDist && table.prevAux !== (fills[i] as number)) {
					prodEv[d] = (prodEv[d] as number) + 1;
					prodDist[i] = d;
					table.extra[table.lastSlot] = Math.min(
						65535,
						(table.extra[table.lastSlot] as number) + 1
					);
				}
				evHist[d] = (evHist[d] as number) + 1;
				obsHist[d] = (obsHist[d] as number) + 1;
			} else {
				obsHist[lmax] = (obsHist[lmax] as number) + 1; // censored at lmax
			}
		} else {
			const t = Math.min(a, lmax);
			obsHist[t] = (obsHist[t] as number) + 1;
		}
	}
	return {
		curve: km(evHist, obsHist, lmax, total),
		dist,
		prodCurve: prodEv ? km(prodEv, obsHist, lmax, total) : undefined,
		prodDist,
		table,
	};
}

/** Kaplan–Meier from histograms of event distances and observation times. */
export function km(
	evHist: Float64Array,
	obsHist: Float64Array,
	lmax: number,
	total: number
): RecurrenceCurve {
	const atRisk = new Float64Array(lmax + 1);
	let run = 0;
	for (let t = lmax; t >= 0; t--) {
		run += obsHist[t] as number;
		atRisk[t] = run;
	}
	const S = new Float64Array(lmax + 1);
	const varS = new Float64Array(lmax + 1);
	S[0] = 1;
	let gw = 0;
	for (let t = 1; t <= lmax; t++) {
		const nt = atRisk[t] as number;
		const dt = evHist[t] as number;
		const h = nt > 0 ? dt / nt : 0;
		S[t] = (S[t - 1] as number) * (1 - h);
		if (nt > dt && dt > 0) gw += dt / (nt * (nt - dt));
		varS[t] = (S[t] as number) ** 2 * gw;
	}
	return { lmax, atRisk, events: evHist, S, varS, n: total };
}

/** R(ℓ) = 1 − S(ℓ). */
export const recurrenceAt = (c: RecurrenceCurve, lag: number): number =>
	1 - (c.S[Math.min(Math.max(lag, 0), c.lmax)] as number);

/** Largest lag with at least `minAtRisk` positions at risk. */
export function estimableLag(c: RecurrenceCurve, minAtRisk = 30): number {
	let best = 0;
	for (let t = 1; t <= c.lmax; t++) if ((c.atRisk[t] as number) >= minAtRisk) best = t;
	return best;
}

export interface SeedEquivalent {
	/** Expected repeated share of an n0-position text, (1/n0)·Σ_{k<n0} R(k), at the effective size n1. */
	value: number;
	/** Effective reference size actually used (n0 unless the lookback is short). */
	n1: number;
	shortLookback: boolean;
	/** Multiply κ by this (n1/n0). */
	kappaFactor: number;
}

/**
 * The seed statistic at its own reference size n0, derived exactly from the curve (§3.3 step 5).
 * When fewer than `minAtRisk` positions are at risk at lag n0−1, evaluates at the largest
 * estimable lag ℓ* (equal to the seed statistic at the text's own length).
 */
export function seedEquivalent(c: RecurrenceCurve, n0: number, minAtRisk = 30): SeedEquivalent {
	const need = n0 - 1;
	const lstar = Math.min(estimableLag(c, minAtRisk), need);
	const n1 = lstar >= need ? n0 : lstar + 1;
	let sum = 0;
	for (let k = 0; k < n1; k++) sum += recurrenceAt(c, k);
	const short = n1 < n0;
	return { value: n1 > 0 ? sum / n1 : 0, n1, shortLookback: short, kappaFactor: n1 / n0 };
}

/** Value R(L) at a single long-range lag, evaluated at the largest estimable lag when short. */
export function longRangeAt(
	c: RecurrenceCurve,
	lref: number,
	minAtRisk = 30
): { value: number; lag: number; shortLookback: boolean; kappaFactor: number } {
	const lag = Math.min(lref, estimableLag(c, minAtRisk));
	return {
		value: recurrenceAt(c, lag),
		lag,
		shortLookback: lag < lref,
		kappaFactor: Math.min(1, lag / lref),
	};
}
